/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 🔐 Permission Catalog — single source of truth for every permission
/// in the application.
///
/// A permission is a string key shaped `<resource>:<action>`. Roles
/// (defined in `builtInRoles.ts` and per-account custom roles) reference
/// these keys. Pages, components, middleware, and server endpoints all
/// gate behavior on the same keys, so the only way to add a new
/// permissioned surface is to add a key here first.
///
/// Conventions:
///   - resource is plural lowercase (`users`, `worksheets`, `roles`).
///   - action is the verb in lowercase (`view`, `create`, `edit`,
///     `delete`, `assign`, `configure`, `impersonate`).
///   - Permissions in the `super:*` namespace are SuperAdmin-only — they
///     unlock cross-account capabilities or operations that act on
///     Pulsar's own infrastructure (encryption keys, tenant-wide reads).
///     Account-scoped admins NEVER receive these regardless of the
///     custom permissions an admin checks for them in the UI.
///
/// Adding a new permission: add it here, group it under the right
/// category, then reference it via the PERMISSIONS export. The roles UI
/// reads `PERMISSION_CATEGORIES` to render a checklist grouped by
/// category, so a new permission appears automatically in the editor.
/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export interface PermissionDefinition {
  /** Stable key used in role docs and code (`users:create`). Never rename. */
  key: string
  /** Short human label shown in the role editor. */
  label: string
  /** Long description shown next to the checkbox in the role editor. */
  description: string
  /** True for permissions that may only be granted to SuperAdmins. */
  superAdminOnly?: boolean
  /** Permissions that should auto-grant when this is granted. */
  implies?: string[]
}

export interface PermissionCategory {
  /** Stable category key. */
  key: string
  /** Display label for the section header. */
  label: string
  /** One-line description shown beneath the section header. */
  description: string
  permissions: PermissionDefinition[]
}

export const PERMISSION_CATEGORIES: PermissionCategory[] = [
  {
    key: 'dashboards',
    label: 'Dashboards & Reporting',
    description: 'Read-only access to overview, observability, query history, and cost dashboards.',
    permissions: [
      { key: 'dashboards:view', label: 'View Dashboards', description: 'Open the overview, query history, and observability dashboards.' },
      { key: 'cost:view', label: 'View Cost Dashboards', description: 'See cost trends, anomalies, and recommendations.' },
      { key: 'observability:view', label: 'View Observability', description: 'Open the system health and observability views.' },
    ],
  },
  {
    key: 'governance',
    label: 'Governance',
    description: 'PII tagging, RBAC graph, and lineage exploration.',
    permissions: [
      { key: 'governance:view', label: 'View Governance', description: 'Open governance pages (PII tags, RBAC graph, lineage).' },
      { key: 'governance:edit', label: 'Edit Governance', description: 'Apply PII tags, modify policy tags, edit governance metadata.', implies: ['governance:view'] },
    ],
  },
  {
    key: 'ingest',
    label: 'Ingest & Pipelines',
    description: 'Pipeline configuration, schema mapping, and quality guardrails.',
    permissions: [
      { key: 'ingest:view', label: 'View Ingest', description: 'See configured pipelines and ingest jobs.' },
      { key: 'ingest:edit', label: 'Configure Ingest', description: 'Create, edit, and delete pipelines and schema mappings.', implies: ['ingest:view'] },
      { key: 'pipelines:run', label: 'Run Pipelines', description: 'Manually trigger a pipeline run.', implies: ['ingest:view'] },
    ],
  },
  {
    key: 'worksheets',
    label: 'Worksheets',
    description: 'Authoring and running SQL worksheets.',
    permissions: [
      { key: 'worksheets:view', label: 'View Worksheets', description: 'Open and read worksheets shared in the account.' },
      { key: 'worksheets:create', label: 'Create Worksheets', description: 'Create new worksheets.', implies: ['worksheets:view'] },
      { key: 'worksheets:edit', label: 'Edit Worksheets', description: 'Modify worksheets owned by the user or shared with edit access.', implies: ['worksheets:view'] },
      { key: 'worksheets:run', label: 'Run Worksheets', description: 'Execute SQL contained in worksheets against the connected Snowflake account.', implies: ['worksheets:view'] },
      { key: 'worksheets:delete', label: 'Delete Worksheets', description: 'Permanently delete worksheets.', implies: ['worksheets:view'] },
    ],
  },
  {
    key: 'dataflow',
    label: 'Data Flow / Workflows',
    description: 'Workflow editor, executions, credentials.',
    permissions: [
      { key: 'dataflow:view', label: 'View Data Flow', description: 'Open the data flow canvas and execution history.' },
      { key: 'dataflow:edit', label: 'Edit Workflows', description: 'Create and modify workflows in the canvas.', implies: ['dataflow:view'] },
      { key: 'dataflow:run', label: 'Run Workflows', description: 'Trigger or schedule workflow executions.', implies: ['dataflow:view'] },
      { key: 'credentials:view', label: 'View Credentials', description: 'See which credentials are configured (does NOT reveal secret values).' },
      { key: 'credentials:edit', label: 'Manage Credentials', description: 'Create, edit, and delete connector credentials.', implies: ['credentials:view'] },
    ],
  },
  {
    key: 'users',
    label: 'Users',
    description: 'User directory and role assignments inside the account.',
    permissions: [
      { key: 'users:view', label: 'View Users', description: 'Open the user-management page and see members of this account.' },
      { key: 'users:invite', label: 'Invite Users', description: 'Send invitations to add new users to this account.', implies: ['users:view'] },
      { key: 'users:edit', label: 'Edit Users', description: 'Update user profile fields like display name and status.', implies: ['users:view'] },
      { key: 'users:assign-role', label: 'Assign Roles', description: 'Change which role a user is assigned within this account.', implies: ['users:view'] },
      { key: 'users:delete', label: 'Remove Users', description: 'Remove a user from this account. Does not delete the underlying Firebase Auth user.', implies: ['users:view'] },
    ],
  },
  {
    key: 'roles',
    label: 'Roles & Permissions',
    description: 'The roles users can be assigned to.',
    permissions: [
      { key: 'roles:view', label: 'View Roles', description: 'See the list of roles defined in this account and the permissions each grants.' },
      { key: 'roles:create', label: 'Create Roles', description: 'Create new custom roles.', implies: ['roles:view'] },
      { key: 'roles:edit', label: 'Edit Roles', description: 'Modify a custom role’s permissions, name, or description. Built-in roles are always read-only.', implies: ['roles:view'] },
      { key: 'roles:delete', label: 'Delete Roles', description: 'Delete custom roles. Built-in roles cannot be deleted.', implies: ['roles:view'] },
    ],
  },
  {
    key: 'billing',
    label: 'Billing',
    description: 'Subscription, invoices, and payment methods.',
    permissions: [
      { key: 'billing:view', label: 'View Billing', description: 'See current plan, invoices, and usage charges for this account.' },
      { key: 'billing:edit', label: 'Manage Billing', description: 'Update payment method, change plan, manage invoice contacts.', implies: ['billing:view'] },
    ],
  },
  {
    key: 'snowflake',
    label: 'Snowflake Engine',
    description: 'Connection, system role/user, and engine configuration.',
    permissions: [
      { key: 'snowflake:view', label: 'View Snowflake Settings', description: 'See the configured Snowflake account, system role, system user, and engine status.' },
      { key: 'snowflake:configure', label: 'Configure Snowflake', description: 'Modify the Snowflake connection, change auth method, rotate credentials, advance the setup wizard.', implies: ['snowflake:view'] },
      { key: 'snowflake:engine', label: 'Manage Engine', description: 'Install, upgrade, or remove the Pulsar engine inside the Snowflake account.', implies: ['snowflake:view', 'snowflake:configure'] },
    ],
  },
  {
    key: 'account',
    label: 'Account Settings',
    description: 'Account-wide settings outside of users, roles, and billing.',
    permissions: [
      { key: 'account:view', label: 'View Account Settings', description: 'See account-level configuration (name, owner, timezone defaults, branding).' },
      { key: 'account:edit', label: 'Edit Account Settings', description: 'Modify account-level configuration.', implies: ['account:view'] },
      { key: 'audit:view', label: 'View Audit Log', description: 'Read the audit log of admin actions taken inside this account.' },
    ],
  },

  // ────────────────────────────────────────────────────────────────────
  // SuperAdmin-only permissions. These are NEVER assignable to a
  // tenant-scope role — they only take effect for users with a doc in
  // /superAdmins/{uid}. The roles editor will surface these as
  // disabled-and-explained for non-SuperAdmins.
  // ────────────────────────────────────────────────────────────────────
  {
    key: 'super',
    label: 'SuperAdmin (Pulsar Internal)',
    description: 'Cross-account capabilities reserved for Pulsar staff. Granted via /superAdmins/{uid} only.',
    permissions: [
      { key: 'super:tenants:view', label: 'View All Tenants', description: 'List every tenant account, regardless of membership.', superAdminOnly: true },
      { key: 'super:tenants:impersonate', label: 'Impersonate Tenant', description: 'Switch the active account context to any tenant for support purposes.', superAdminOnly: true, implies: ['super:tenants:view'] },
      { key: 'super:encrypt', label: 'Encrypt Strings', description: 'Use the Pulsar master key to encrypt arbitrary strings (used when seeding tenant credentials manually).', superAdminOnly: true },
      { key: 'super:decrypt', label: 'Decrypt Strings', description: 'Use the Pulsar master key to decrypt stored ciphertext. Highly privileged.', superAdminOnly: true },
      { key: 'super:admins:manage', label: 'Manage SuperAdmins', description: 'Add or remove other SuperAdmins from /superAdmins.', superAdminOnly: true },
      { key: 'super:audit:view', label: 'View Global Audit Log', description: 'Read the cross-tenant audit log of SuperAdmin actions.', superAdminOnly: true },
    ],
  },
]

/**
 * Flat lookup of every permission key → its definition. Use this when
 * you want to render a label/description for a stored permission key.
 */
export const PERMISSIONS_BY_KEY: Record<string, PermissionDefinition> = PERMISSION_CATEGORIES.reduce(
  (acc, category) => {
    for (const perm of category.permissions) acc[perm.key] = perm
    return acc
  },
  {} as Record<string, PermissionDefinition>,
)

/** Every permission key that exists. Source of truth for typeahead and validation. */
export const ALL_PERMISSIONS: string[] = Object.keys(PERMISSIONS_BY_KEY)

/** Every account-scoped permission key (excludes super:*). Used to seed the Admin role. */
export const ALL_ACCOUNT_PERMISSIONS: string[] = ALL_PERMISSIONS.filter(
  key => !PERMISSIONS_BY_KEY[key]?.superAdminOnly,
)

/** Every SuperAdmin-only permission key. Used to seed the SuperAdmin pseudo-role. */
export const ALL_SUPER_PERMISSIONS: string[] = ALL_PERMISSIONS.filter(
  key => PERMISSIONS_BY_KEY[key]?.superAdminOnly,
)

/**
 * Expand a permission set with everything it implies.
 *
 * `governance:edit` implies `governance:view`. Storing the implied
 * permissions explicitly avoids the implication graph mattering at
 * read time — every consumer just does `set.has(key)`.
 */
export function expandImplied(permissionKeys: Iterable<string>): string[] {
  const result = new Set<string>()
  const stack = [...permissionKeys]
  while (stack.length) {
    const key = stack.pop()!
    if (result.has(key)) continue
    result.add(key)
    const def = PERMISSIONS_BY_KEY[key]
    if (def?.implies) stack.push(...def.implies)
  }
  return [...result]
}

/**
 * Type alias for any valid permission key. Hand-typed as a string union
 * would be ideal, but the catalog is dynamic — we settle for the runtime
 * `ALL_PERMISSIONS` check via `isPermissionKey()`.
 */
export type PermissionKey = string

export function isPermissionKey(key: unknown): key is PermissionKey {
  return typeof key === 'string' && key in PERMISSIONS_BY_KEY
}
